import type { BoundingBox, LatLon } from "@radar-sim/schema";
import { normaliseBearing, projectPosition } from "../geodesy.js";
import { mulberry32, seedFor } from "./rng.js";
import type {
  BehaviourImpl,
  BehaviourPose,
  BehaviourRuntime,
} from "./types.js";

export interface RandomWalkParams {
  bounds: BoundingBox;
  speedRangeKnots: readonly [number, number];
}

const SECONDS_PER_HOUR = 3600;
/** How often the random-walk vessel reconsiders its course/speed. */
const DECISION_INTERVAL_SECONDS = 30;
/** Max course change per decision tick, in degrees. */
const MAX_COURSE_CHANGE_DEG = 30;

/**
 * Wandering vessel — picks a new course and speed every
 * DECISION_INTERVAL_SECONDS, constrained to stay within the given bounds.
 * Uses a seeded RNG (vesselId + scenarioId) so the walk is deterministic.
 */
export const RandomWalk: BehaviourImpl<RandomWalkParams> = {
  name: "random_walk",
  version: "1.0.0",
  init({ params, spawn, vesselId, scenarioId, spawnSimSeconds }): BehaviourRuntime {
    const seed = seedFor("random_walk", scenarioId, vesselId);
    return new RandomWalkRuntime(params, spawn, spawnSimSeconds, seed);
  },
};

class RandomWalkRuntime implements BehaviourRuntime {
  private readonly params: RandomWalkParams;
  private readonly spawn: BehaviourPose;
  private readonly spawnSimSeconds: number;
  private readonly seed: number;

  // Cached pose computed lazily and extended forward as time advances.
  private cursorSeconds: number;
  private cursor: BehaviourPose;
  private rng: () => number;

  constructor(
    params: RandomWalkParams,
    spawn: BehaviourPose,
    spawnSimSeconds: number,
    seed: number,
  ) {
    this.params = params;
    this.spawn = spawn;
    this.spawnSimSeconds = spawnSimSeconds;
    this.seed = seed;
    this.cursorSeconds = spawnSimSeconds;
    this.cursor = { ...spawn };
    this.rng = mulberry32(seed);
  }

  poseAt(simSeconds: number): BehaviourPose {
    if (simSeconds < this.cursorSeconds) {
      // Replay-style backwards seek: rebuild from spawn.
      this.cursorSeconds = this.spawnSimSeconds;
      this.cursor = { ...this.spawn };
      this.rng = mulberry32(this.seed);
    }
    while (this.cursorSeconds < simSeconds) {
      const stepEnd = Math.min(
        simSeconds,
        nextDecisionTime(this.cursorSeconds, this.spawnSimSeconds),
      );
      const dt = stepEnd - this.cursorSeconds;
      const distNm = this.cursor.sog * (dt / SECONDS_PER_HOUR);
      const moved =
        distNm > 0
          ? projectPosition(this.cursor.position, this.cursor.cog, distNm)
          : this.cursor.position;
      this.cursor = { ...this.cursor, position: moved };
      this.cursorSeconds = stepEnd;
      if (
        Math.abs(
          this.cursorSeconds -
            nextDecisionTime(this.cursorSeconds - 0.001, this.spawnSimSeconds),
        ) < 1e-6
      ) {
        // Exactly on a decision boundary: reroll course/speed.
        this.cursor = this.reroll(this.cursor);
      }
    }
    return { ...this.cursor };
  }

  private reroll(pose: BehaviourPose): BehaviourPose {
    const [lo, hi] = this.params.speedRangeKnots;
    const newSog = lo + this.rng() * (hi - lo);
    const courseDelta = (this.rng() - 0.5) * 2 * MAX_COURSE_CHANGE_DEG;
    let newCog = normaliseBearing(pose.cog + courseDelta);
    // If outside bounds, point roughly back toward the centre.
    if (!withinBounds(pose.position, this.params.bounds)) {
      const centre = boundsCentre(this.params.bounds);
      newCog = normaliseBearing(headingTowards(pose.position, centre));
    }
    return { ...pose, sog: newSog, cog: newCog };
  }
}

function nextDecisionTime(t: number, spawn: number): number {
  const elapsed = t - spawn;
  const idx = Math.floor(elapsed / DECISION_INTERVAL_SECONDS) + 1;
  return spawn + idx * DECISION_INTERVAL_SECONDS;
}

function withinBounds(p: LatLon, b: BoundingBox): boolean {
  return (
    p.lat <= b.nw.lat &&
    p.lat >= b.se.lat &&
    p.lon >= b.nw.lon &&
    p.lon <= b.se.lon
  );
}

function boundsCentre(b: BoundingBox): LatLon {
  return {
    lat: (b.nw.lat + b.se.lat) / 2,
    lon: (b.nw.lon + b.se.lon) / 2,
  };
}

function headingTowards(from: LatLon, to: LatLon): number {
  const dLat = to.lat - from.lat;
  const dLon = (to.lon - from.lon) * Math.cos((from.lat * Math.PI) / 180);
  return normaliseBearing((Math.atan2(dLon, dLat) * 180) / Math.PI);
}
